import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExternalLink, Loader2, Lock, ShieldCheck } from "lucide-react";
import * as React from "react";
import { useState } from "react";

interface IyzicoCheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentPageUrl: string | null;
  title?: string;
  description?: string;
}

export function IyzicoCheckoutDialog({
  open,
  onOpenChange,
  paymentPageUrl,
  title = "Güvenli Ödeme",
  description = "256-bit SSL ve 3D Secure güvencesiyle ödemenizi tamamlayın.",
}: IyzicoCheckoutDialogProps) {
  const [iframeLoading, setIframeLoading] = useState(true);

  React.useEffect(() => {
    if (open) {
      setIframeLoading(true);
    }
  }, [open]);

  if (!paymentPageUrl) return null;

  // Append iframe=true to optimize iyzico layout inside an iframe
  const iframeSrc = paymentPageUrl.includes("?")
    ? `${paymentPageUrl}&iframe=true`
    : `${paymentPageUrl}?iframe=true`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[95vw] max-w-2xl overflow-hidden rounded-2xl border bg-background p-0 shadow-2xl sm:w-full">
        <DialogHeader className="border-b bg-muted/30 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1 text-left">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <DialogTitle className="font-bold text-foreground text-lg">
                  {title}
                </DialogTitle>
              </div>
              <DialogDescription className="font-medium text-muted-foreground text-xs">
                {description}
              </DialogDescription>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="hidden text-muted-foreground text-xs hover:text-foreground sm:inline-flex"
              onClick={() =>
                window.open(paymentPageUrl, "_blank", "noopener,noreferrer")
              }
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Yeni Sekmede Aç
            </Button>
          </div>
        </DialogHeader>

        <div className="relative min-h-[620px] w-full bg-background">
          {iframeLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background/90 backdrop-blur-xs">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="font-medium text-muted-foreground text-sm">
                Güvenli ödeme formu hazırlanıyor...
              </p>
            </div>
          )}

          <iframe
            src={iframeSrc}
            title="İyzico Güvenli Ödeme"
            className="h-[620px] w-full border-0"
            onLoad={() => setIframeLoading(false)}
            allow="payment"
          />
        </div>

        <div className="flex items-center justify-between border-t bg-muted/20 px-6 py-3 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Lock className="h-3 w-3 text-emerald-500" />
            <span>256-Bit SSL ile Şifrelenmiş Bağlantı</span>
          </div>
          <span className="font-semibold uppercase tracking-wider">
            iyzico ile Güvenli Öde
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
